/* eslint-disable no-nested-ternary */
/* eslint-disable no-case-declarations */
import React, { useEffect, useMemo, useState } from "react";

import { Grid, IconButton, MenuItem, SelectChangeEvent, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { AiOutlineCloseCircle, AiOutlinePlusCircle } from "react-icons/ai";
import { FiInfo } from "react-icons/fi";
import { isValidPhoneNumber } from "react-phone-number-input";
import isEmail from "validator/lib/isEmail";
import * as yup from "yup";

import AutoSuggest from "../../../../../components/Form/AutoSuggest";
import Checkbox from "../../../../../components/Form/Checkbox";
import Input from "../../../../../components/Form/Input";
import Select from "../../../../../components/Form/Select";
import ActionButtons from "../../../../../components/Generic/ActionButtons";
import { useAppState } from "../../../../../context/StateContext";
import useYupValidation from "../../../../../hooks/useYupValidation";
import { Agent } from "../../../../../models/agent";
import { Carrier } from "../../../../../models/carrier";
import { Contact } from "../../../../../models/contact";
import { Customer } from "../../../../../models/customer";
import {
  ShipmentsTracking,
  Communication,
  CustomerDetails,
  CustomerShipmentTracking,
} from "../../../../../models/shipments/shipments.tracking";
import { User } from "../../../../../models/user";
import AgentService from "../../../../../services/agent.service";
import CarrierService from "../../../../../services/carrier.service";
import CustomerService from "../../../../../services/customer.service";
import UserService from "../../../../../services/user.service";
import {
  CarrierTdType,
  CarrierType,
  CommunicationsFromOptions,
  ShipmentAgentType,
  Status,
} from "../../../../../util/enums";
import { Options, getSelectOptions } from "../../../../../util/getStatusOptions";
import {
  onAddUserCommunication,
  onRemoveUserCommunication,
  onUpdatedCustomerCommunication,
} from "./actions/useAction";
import CustomerDetailsComponent from "./components/Customer";
import SkeletonDetails from "./components/Skeleton";
import useStyles, {
  Title,
  Buttons,
  CarrierInfoWrapper,
  InfoSpan,
  CheckIcon,
  CloseIcon,
} from "./styles";

type CommunicationAttribute = {
  userId?: number;
  customerId?: number;
  from: string;
};

type Validation = {
  customers?: CustomerDetails[];
  hasAgent?: boolean;
};

interface DetailsProps {
  shipmentTracking?: ShipmentsTracking;
  isUpdatingAction: boolean;
  onCancel: () => void;
  saveShipmentTracking: (shipmentTracking: ShipmentsTracking) => void;
  updateShipmentTracking: (shipmentTracking: ShipmentsTracking) => void;
  fieldHasError: (field: string) => boolean;
  getErrorByField: (field: string) => string | undefined;
}

const Details = ({
  shipmentTracking,
  updateShipmentTracking,
  fieldHasError,
  getErrorByField,
  isUpdatingAction,
  saveShipmentTracking,
  onCancel,
}: DetailsProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { currentUser } = useAppState();

  const [contacts, setContacts] = useState<Contact[]>([]);

  const [customerErrors, setCustomerErrors] = useState<yup.ValidationError[]>([]);
  const [customerList, setCustomerList] = useState<Customer[]>([]);
  const [usersList, setUserList] = useState<User[]>([]);
  const [fieldError, setFieldError] = useState<string>("");
  const [shipmentHasPartner, setShipmentHasPartner] = useState<boolean>(false);
  const [agentList, setAgentList] = useState<Agent[]>([]);
  const [contactError, setContactError] = useState<string>();

  const [carrierField, setCarrierField] = useState<string>("");
  const [carrierType, setCarrierType] = useState<Carrier>();

  const isCreate = !shipmentTracking?.id;

  const alignItems = fieldError.length > 0 ? "center" : "flex-end";

  const { fieldHasError: customerHasErrors, getErrorByField: getCustomerErrors } =
    useYupValidation(customerErrors);

  const [communications, setCommunications] = useState<Communication[]>([]);
  const [customers, setCustomers] = useState<CustomerDetails[]>(
    shipmentTracking?.customersToShipment ?? [
      {
        email: "",
        phone: "",
      } as CustomerDetails,
    ]
  );

  const usersCommunications = useMemo(
    () =>
      communications.filter(
        (communication) => !communication.customerId && !communication.contactId
      ),
    [communications]
  );
  const customersCommunications = useMemo(
    () => communications.filter((communication) => !!communication.customerId),
    [communications]
  );
  const contactCommunication = useMemo(
    () => communications.filter((communication) => !!communication.contactId),
    [communications]
  );

  const { modeOptions, viaOptions } = getSelectOptions();

  const hasUserAndCompanyAccessToPartnerField =
    !!currentUser?.companyId &&
    currentUser?.company?.allowAssociateAgent &&
    !!currentUser?.resources.find((resource) => resource.name === "Network");

  useQuery(
    ["customer.list"],
    () =>
      CustomerService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        include: JSON.stringify([{ association: "timezone" }]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          setCustomerList(response.data);
        }
      },
    }
  );

  useQuery(
    ["user.list"],
    () =>
      UserService.getAll({
        status: Status.ACTIVE,
        include: JSON.stringify([
          {
            association: "usersCompanies",
            required: true,
            include: [{ association: "role" }],
            where: {
              companyId: currentUser?.companyId,
            },
          },
        ]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setUserList(response.data);
        }
      },
    }
  );

  useQuery(
    ["agent.list"],
    () =>
      AgentService.getAll({
        createdCompanyId: currentUser?.companyId,
        status: Status.ACTIVE,
        include: JSON.stringify([{ association: "contacts" }]),
      }),
    {
      enabled: hasUserAndCompanyAccessToPartnerField ?? false,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setAgentList(response.data);
      },
    }
  );

  const { data: carrierListResponse } = useQuery(
    ["list.carrier"],
    () =>
      CarrierService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        type: CarrierType.SEA,
        include: JSON.stringify([{ association: "trackableBy" }]),
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
    }
  );

  const schema = yup.object().shape({
    hasAgent: yup.boolean(),
    customers: yup.array().when("hasAgent", {
      is: false,
      then: yup.array().of(
        yup.object().shape({
          order: yup.string(),
          customer: yup.object().shape({
            name: yup
              .string()
              .trim()
              .typeError(translate("thisFieldIsRequired"))
              .required(translate("thisFieldIsRequired"))
              .test("empty-check", translate("thisFieldIsRequired"), (name) =>
                name ? name?.length > 0 : false
              ),
            lastName: yup
              .string()
              .trim()
              .typeError(translate("thisFieldIsRequired"))
              .required(translate("thisFieldIsRequired"))
              .test("empty-check", translate("thisFieldIsRequired"), (lastName) =>
                lastName ? lastName?.length > 0 : false
              ),
            email: yup
              .string()
              .trim()
              .typeError(translate("thisFieldIsRequired"))
              .required(translate("thisFieldIsRequired"))
              .test("email-check", translate("thisFieldIsRequired"), (email) =>
                email ? isEmail(email) : false
              ),
            phone: yup
              .string()
              .trim()
              .typeError(translate("thisFieldIsRequired"))
              .required(translate("thisFieldIsRequired"))
              .test("phone-check", translate("thisFieldIsRequired"), (phone) =>
                phone ? isValidPhoneNumber(phone) : false
              ),
          }),
        })
      ),
      otherwise: yup.array().of(
        yup.object().shape({
          order: yup.string(),
          customer: yup.object().shape({
            name: yup
              .string()
              .trim()
              .typeError(translate("thisFieldIsRequired"))
              .required(translate("thisFieldIsRequired"))
              .test("empty-check", translate("thisFieldIsRequired"), (name) =>
                name ? name?.length > 0 : false
              ),
            lastName: yup
              .string()
              .trim()
              .typeError(translate("thisFieldIsRequired"))
              .required(translate("thisFieldIsRequired"))
              .test("empty-check", translate("thisFieldIsRequired"), (lastName) =>
                lastName ? lastName?.length > 0 : false
              ),
            email: yup.string().trim(),
            phone: yup.string().trim(),
          }),
        })
      ),
    }),
  });

  const handleValidateCustomers = ({ customers, hasAgent = false }: Validation): boolean => {
    try {
      schema.validateSync(
        {
          hasAgent,
          customers,
        },
        { abortEarly: false }
      );
      setCustomerErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setCustomerErrors(error?.inner);
      }

      return false;
    }
  };

  const handleChangeCommunication = (attribute: string, index: number, value?: string | number) => {
    if (!value && attribute === "userId") {
      (usersCommunications[index] as any)[attribute as keyof CommunicationAttribute] = 0;
      setFieldError(translate("thisFieldIsRequired"));

      return;
    }

    if (usersCommunications && index !== undefined) {
      (usersCommunications[index] as any)[attribute as keyof CommunicationAttribute] = value;

      if (usersCommunications[index].userId && usersCommunications[index].from !== "")
        setFieldError("");

      setCommunications([
        ...customersCommunications,
        ...usersCommunications,
        ...contactCommunication,
      ]);

      if (attribute === "from") {
        updateShipmentTracking({
          ...shipmentTracking,
          communications: [
            ...customersCommunications,
            ...usersCommunications,
            ...contactCommunication,
          ],
        });
      }
    }
  };

  const getUserName = (userId?: number, user?: User) => {
    if (user && userId) {
      return `${user.name ?? ""} ${user.lastName ?? ""}`;
    }

    if (userId) {
      const foundedUser = usersList.find((item) => Number(userId) === item.id);

      return `${foundedUser?.name ?? ""} ${foundedUser?.lastName ?? ""}`;
    }

    return "";
  };

  const validateCommunication = () => {
    let customersToUpdate = shipmentTracking?.customersToShipment;
    let communicationsToUpdate = shipmentTracking?.communications;

    if (shipmentTracking?.communications?.length) {
      communicationsToUpdate = shipmentTracking.communications.filter(
        (communication) => communication.userId !== 0 && communication.from !== ""
      );
    }
    if (shipmentTracking?.customersToShipment?.length) {
      customersToUpdate = shipmentTracking?.customersToShipment?.filter(
        (customer) => !!customer?.customer
      );
    }

    return {
      ...shipmentTracking,
      communications: communicationsToUpdate,
      customers: customersToUpdate,
    };
  };

  const validateAgent = () => {
    if (shipmentTracking?.shipmentAgents?.length) {
      const agent = shipmentTracking.shipmentAgents?.[0]?.agent;

      if (agent && agent.contacts?.length && !contactCommunication.length) {
        setContactError(translate("thisFieldIsRequired"));

        return false;
      }
    }
    setContactError("");

    return true;
  };

  const verifyCustomerDetails = (customersDetails?: CustomerDetails[]) => {
    if (customersDetails) {
      const customersVerified = _.cloneDeep(customersDetails);

      for (const customerDetail of customersVerified) {
        if (!customerDetail.customer?.email) {
          customerDetail.customer = { ...customerDetail.customer, email: "" };
        }

        if (!customerDetail.customer?.phone) {
          customerDetail.customer = { ...customerDetail.customer, phone: "" };
        }
      }

      return customersVerified;
    }

    return [];
  };

  const handleSave = () => {
    const shipmentTrackingToUpdate = validateCommunication();
    const newShipmentTracking = updateShipmentTrackingByCode(shipmentTrackingToUpdate);

    if (hasUserAndCompanyAccessToPartnerField && !validateAgent()) return;

    if (shipmentTracking?.customersToShipment) {
      if (
        handleValidateCustomers({
          customers: shipmentTracking?.customersToShipment,
          hasAgent: !!shipmentTracking?.shipmentAgents?.length,
        })
      ) {
        saveShipmentTracking({
          ...newShipmentTracking,
          customersToShipment: verifyCustomerDetails(newShipmentTracking.customersToShipment),
          customers: verifyCustomerDetails(newShipmentTracking.customers),
        } as ShipmentsTracking);
      }
    } else {
      saveShipmentTracking(newShipmentTracking as ShipmentsTracking);
    }
  };

  const renderAddOrDeleteUserCommunication = (index: number) => {
    const isTheLastCommunicationFields = index === usersCommunications.length - 1;
    const hasOnlyOneEmptyCommunicationFields =
      communications.filter((communication) => communication.userId === 0)?.length === 1;

    return (
      <Grid
        item
        container
        alignItems={alignItems}
        xs={12}
        sm={2}
        md={2}
        lg={2}
        className={classes.iconsContainer}
      >
        {!hasOnlyOneEmptyCommunicationFields && (
          <Grid item>
            <IconButton
              className={classes.iconButton}
              onClick={() =>
                onRemoveUserCommunication(usersCommunications, index, (communication) => {
                  if (!communication.length) {
                    setCommunications([
                      ...customersCommunications,
                      ...contactCommunication,
                      { userId: 0, from: "" },
                    ]);
                    updateShipmentTracking({
                      ...shipmentTracking,
                      communications: [...customersCommunications, ...contactCommunication],
                    });
                  } else {
                    setCommunications([
                      ...customersCommunications,
                      ...usersCommunications,
                      ...contactCommunication,
                    ]);

                    updateShipmentTracking({
                      ...shipmentTracking,
                      communications: [
                        ...customersCommunications,
                        ...usersCommunications,
                        ...contactCommunication,
                      ],
                    });
                  }
                })
              }
            >
              <AiOutlineCloseCircle color="#F97066" className={classes.icon} />
            </IconButton>
          </Grid>
        )}
        {isTheLastCommunicationFields && (
          <Grid item>
            <IconButton
              className={classes.iconButton}
              onClick={() =>
                onAddUserCommunication(
                  usersCommunications,
                  () => setFieldError(translate("thisFieldIsRequired")),
                  (newCommunication) => {
                    setCommunications([
                      ...customersCommunications,
                      ...contactCommunication,
                      ...usersCommunications,
                      newCommunication,
                    ]);
                  }
                )
              }
            >
              <AiOutlinePlusCircle color="#308CDB" className={classes.icon} />
            </IconButton>
          </Grid>
        )}
      </Grid>
    );
  };

  const getUserAutosuggestValue = (index: number) => {
    const userCommunications = communications.filter(
      (communication) => !communication.customerId && !communication.contactId
    );
    const userFrom = userCommunications[index]?.from;

    if (userFrom === CommunicationsFromOptions.EMAIL || userFrom === CommunicationsFromOptions.SMS)
      return viaOptions.filter((option) => option.value === userFrom);
    if (userFrom === CommunicationsFromOptions.BOTH) return viaOptions;

    return [];
  };

  const onUpdateAgentCommunication = (contactsField: Contact[]) => {
    setContacts(contactsField);
    let allCommunication = [...customersCommunications, ...usersCommunications];

    if (contactsField.length) {
      setContactError("");
      const newCommunications = contactsField.map((contact) => ({
        contactId: contact.id,
        from: "email",
      }));

      allCommunication =
        allCommunication && allCommunication.length
          ? [...allCommunication, ...newCommunications]
          : newCommunications;
    }

    setCommunications(allCommunication);
    updateShipmentTracking({
      ...shipmentTracking,
      communications: allCommunication,
    });
  };

  const onAddOrRemoveAgent = (agent?: Agent) => {
    if (agent)
      updateShipmentTracking({
        ...shipmentTracking,
        shipmentAgents: [
          {
            agentId: agent.id,
            agent,
            type: ShipmentAgentType.DESTINATION,
          },
        ],
      });
    else {
      setContacts([]);
      updateShipmentTracking({
        ...shipmentTracking,
        shipmentAgents: [],
        communications: [...usersCommunications, ...customersCommunications],
      });
    }
  };

  const renderAgentFields = () => {
    if (!hasUserAndCompanyAccessToPartnerField) return null;

    return (
      <React.Fragment>
        <Title item xs={12} sm={12} md={12} lg={12}>
          <span>{translate("agent")}</span>
        </Title>
        <Grid item xs={12} className={classes.mb16}>
          <Checkbox
            label={translate("thisShipmentHasAnAgent")}
            checked={shipmentHasPartner}
            disabled={!hasUserAndCompanyAccessToPartnerField}
            onChange={(event) => {
              setShipmentHasPartner(!!event.target.checked);
              if (!event.target.checked) {
                onAddOrRemoveAgent();
              }
            }}
          />
        </Grid>
        {shipmentHasPartner && (
          <Grid item container xs={12} md={6} lg={6} className={classes.mb16} spacing={2}>
            <Grid item xs={12} md={6} lg={6}>
              <AutoSuggest
                data-cy="autosuggest-partner"
                noOptionsText={translate("searchForAgent")}
                placeholder={translate("agent")}
                name="partner"
                disabled={!hasUserAndCompanyAccessToPartnerField}
                inputValue={shipmentTracking?.shipmentAgents?.[0]?.agent?.entity ?? ""}
                getOptionLabel={(agent: Agent) => {
                  return `${agent?.entity}`;
                }}
                options={agentList}
                getOptionSelected={(option?: Agent, value?: Agent) => option?.id === value?.id}
                onChange={onAddOrRemoveAgent}
              />
            </Grid>
            {shipmentTracking?.shipmentAgents?.[0]?.agent?.contacts?.length ? (
              <Grid item xs={12} md={6} lg={6}>
                <AutoSuggest
                  name="contact"
                  dataCy="contact-field"
                  placeholder={contacts.length ? "" : translate("contact")}
                  noOptionsText={translate("searchForAContact")}
                  disabled={!hasUserAndCompanyAccessToPartnerField}
                  multiple
                  value={contacts}
                  options={shipmentTracking?.shipmentAgents?.[0]?.agent?.contacts}
                  getOptionLabel={(contact: Contact) => {
                    return `${contact.firstName ?? ""} ${contact.lastName ?? ""} - ${
                      contact.email ?? ""
                    }`;
                  }}
                  getOptionSelected={(option?: Contact, value?: Contact) =>
                    option?.id === value?.id
                  }
                  onChange={(contacts: Contact[]) => {
                    if (contacts) onUpdateAgentCommunication(contacts);
                    else onUpdateAgentCommunication([]);
                  }}
                  error={!!contactError?.trim().length}
                  errorMessage={contactError}
                />
              </Grid>
            ) : null}
          </Grid>
        )}
      </React.Fragment>
    );
  };

  const updateShipmentTrackingByCode = (shipment: ShipmentsTracking) => {
    const { billOfLanding, bookingNo, containerNumbers } = shipment;
    const scac = carrierType?.scac;

    const newShipment = {
      ...shipment,
      carrierScac: scac,
    };

    if (billOfLanding && billOfLanding.trim().length > 0) {
      return {
        ...newShipment,
        carrierType: {
          scac,
          tdType: CarrierTdType.BL,
        },
      };
    }

    if (bookingNo && bookingNo.trim().length > 0) {
      return {
        ...newShipment,
        carrierType: {
          scac,
          tdType: CarrierTdType.BK,
        },
      };
    }

    if (containerNumbers && containerNumbers.trim().length > 0) {
      return {
        ...newShipment,
        carrierScac: scac || "AUTO",
        carrierType: {
          scac: scac || "AUTO",
          tdType: CarrierTdType.CN,
        },
      };
    }

    return newShipment;
  };

  const renderStatusIcon = (trackableBy?: string) => {
    const findTrackableBy = carrierType?.trackableBy?.find((tby) => tby.code === trackableBy);

    if (findTrackableBy) return <CheckIcon />;

    return <CloseIcon />;
  };

  const renderCarrierInformation = () => {
    return (
      <Grid item xs={12} className={classes.pt0}>
        <CarrierInfoWrapper>
          <InfoSpan>
            <FiInfo />
            {translate("thisCarrierAllowsShipmentTrackingBy")}:
          </InfoSpan>
          <CarrierInfoWrapper>
            <InfoSpan bold>
              {translate("booking")} #{renderStatusIcon(CarrierTdType.BK)}
            </InfoSpan>
            <InfoSpan bold>
              {translate("billOfLanding")}
              {renderStatusIcon(CarrierTdType.BL)}
            </InfoSpan>
            <InfoSpan bold>
              {translate("container")} #{renderStatusIcon(CarrierTdType.CN)}
            </InfoSpan>
          </CarrierInfoWrapper>
        </CarrierInfoWrapper>
      </Grid>
    );
  };

  useEffect(() => {
    if (shipmentTracking?.shipmentAgents?.length) setShipmentHasPartner(true);
  }, [shipmentTracking?.shipmentAgents]);

  useEffect(() => {
    if (!shipmentTracking?.customersToShipment || !shipmentTracking.customersToShipment.length)
      setCustomers([{ order: "", customer: {} as CustomerShipmentTracking }]);
    else setCustomers(shipmentTracking?.customersToShipment);
  }, [shipmentTracking?.customersToShipment]);

  useEffect(() => {
    const currentUserCommunication = { userId: currentUser?.id, from: "" };
    let isFirstAccess = false;
    let initialCommunication: Communication[] = [currentUserCommunication];

    if (
      shipmentTracking?.communications &&
      shipmentTracking?.communications?.length &&
      !communications.length
    ) {
      isFirstAccess = true;
      const usersCommunications = shipmentTracking.communications?.filter(
        (communication) => !!communication.userId
      );
      const contactCommunications = shipmentTracking.communications?.filter(
        (communication) => !!communication.contactId
      );

      if (!usersCommunications.length) {
        initialCommunication = [...shipmentTracking.communications, currentUserCommunication];
      } else {
        initialCommunication = shipmentTracking.communications;
      }

      if (contactCommunications?.length) {
        const contacts = contactCommunications
          .map((contactCommunication) => contactCommunication.contact!)
          .filter((contact) => !!contact);

        if (contacts) setContacts(contacts);
      }
    } else if (!communications.length) {
      isFirstAccess = true;
    }

    if (isFirstAccess || (!shipmentTracking?.id && !communications.length))
      setCommunications(initialCommunication);
  }, [shipmentTracking?.communications]);

  useEffect(() => {
    if (carrierType) {
      const { name, scac } = carrierType!;

      setCarrierField(`${name ?? ""} - ${scac ?? ""}`);

      updateShipmentTracking({
        ...shipmentTracking,
        carrierScac: scac,
      });
    } else {
      setCarrierField("");
      updateShipmentTracking({
        ...shipmentTracking,
        carrierScac: undefined,
      });
    }
  }, [carrierType]);

  useEffect(() => {
    let timeout;

    if (fieldError.length) {
      timeout = setTimeout(() => setFieldError(""), 5000);
    } else clearTimeout(timeout);
  }, [fieldError]);

  useEffect(() => {
    if (shipmentTracking?.id && carrierListResponse?.data.length) {
      const carrier = carrierListResponse?.data.find(
        (carrier) => carrier.scac === shipmentTracking?.carrierScac
      );

      if (carrier) {
        setCarrierType(carrier);
        setCarrierField(`${carrier.name} - ${carrier.scac}`);
      }
    }
  }, [carrierListResponse, shipmentTracking]);

  return (
    <Grid container direction="column">
      <Title item xs={12} sm={12} md={12} lg={12}>
        <span>{translate("general")}</span>
      </Title>

      <Grid item container direction="row" spacing={3} className={classes.mb16}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Input
            name="containerId"
            label={`${translate("shipment")} #`}
            placeholder="AS123455454"
            value={shipmentTracking?.containerId}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              updateShipmentTracking({ ...shipmentTracking, containerId: event.target.value })
            }
            error={fieldHasError("containerId")}
            errorMessage={getErrorByField("containerId")}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Select
            name="shipmentType"
            label={translate("type")}
            value={shipmentTracking?.shipmentType || ""}
            onChange={(event: SelectChangeEvent<any>) =>
              updateShipmentTracking({ ...shipmentTracking, shipmentType: event.target.value })
            }
            displayEmpty
            placeholder={translate("selectOne")}
            disabled
          >
            {modeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>

      <Grid item container direction="column" spacing={3} className={classes.mb16}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <AutoSuggest
            disabled={!!shipmentTracking?.id}
            label={translate("carrier")}
            name="carrier"
            value={carrierType?.id}
            inputValue={
              shipmentTracking?.id
                ? carrierField
                  ? carrierField || ""
                  : "AUTO"
                : carrierField || ""
            }
            getOptionLabel={(carrier: Carrier) => `${carrier.name} - ${carrier.scac}`}
            getOptionSelected={(option: Carrier, value: Carrier) => option.id === value.id}
            options={carrierListResponse?.data ?? []}
            onChangeInput={(value) => setCarrierField(value)}
            onChange={(carrier: Carrier) => {
              setCarrierType(carrier);
            }}
          />
        </Grid>
        {carrierType ? renderCarrierInformation() : null}
      </Grid>

      <Grid item container direction="row" spacing={3} className={classes.mb16}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Tooltip
            placement="top"
            title={
              shipmentTracking?.id
                ? translate("asTheShipmentIdentificationIsUniqueYouCannotChangeItAfterCreation")
                : ""
            }
          >
            <div>
              <Input
                disabled={!!shipmentTracking?.id}
                name="billOfLanding"
                label={translate("billOfLanding")}
                placeholder={translate("billOfLanding")}
                value={shipmentTracking?.billOfLanding}
                onChange={(event) => {
                  updateShipmentTracking({
                    ...shipmentTracking,
                    billOfLanding: event.target.value,
                  });
                }}
                error={
                  fieldHasError("billOfLanding") ||
                  fieldHasError("newObject") ||
                  fieldHasError("newObject.billOfLanding")
                }
                errorMessage={
                  getErrorByField("billOfLanding") ||
                  getErrorByField("newObject") ||
                  getErrorByField("newObject.billOfLanding")
                }
              />
            </div>
          </Tooltip>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Tooltip
            placement="top"
            title={
              shipmentTracking?.id
                ? translate("asTheShipmentIdentificationIsUniqueYouCannotChangeItAfterCreation")
                : ""
            }
          >
            <div>
              <Input
                disabled={!!shipmentTracking?.id}
                name="booking"
                label={`${translate("booking")} #`}
                placeholder={translate("bookingNumber")}
                value={shipmentTracking?.bookingNo}
                onChange={(event) => {
                  updateShipmentTracking({
                    ...shipmentTracking,
                    bookingNo: event.target.value,
                  });
                }}
                error={
                  fieldHasError("bookingNo") ||
                  fieldHasError("newObject") ||
                  fieldHasError("newObject.bookingNo")
                }
                errorMessage={
                  getErrorByField("bookingNo") ||
                  getErrorByField("newObject") ||
                  getErrorByField("newObject.bookingNo")
                }
              />
            </div>
          </Tooltip>
        </Grid>
      </Grid>

      <Grid item container direction="row" spacing={3} className={classes.mb16}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Input
            disabled={!!shipmentTracking?.id}
            name="containerNumber"
            label={translate("containerNumber")}
            placeholder={"ABC12345678"}
            value={shipmentTracking?.containerNumbers}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              updateShipmentTracking({
                ...shipmentTracking,
                containerNumbers: event.target.value,
              });
            }}
            error={
              fieldHasError("containerNumbers") ||
              fieldHasError("newObject") ||
              fieldHasError("newObject.containerNumbers")
            }
            errorMessage={
              getErrorByField("newObject")
                ? getErrorByField("newObject")
                : getErrorByField("newObject.containerNumbers")
            }
          />
        </Grid>

        {!isCreate && (
          <>
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Input
                name="origin"
                label={translate("origin")}
                placeholder={translate("portName")}
                value={shipmentTracking?.shipmentTracking?.originPort ?? ""}
                disabled
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Input
                name="destination"
                label={translate("destination")}
                placeholder={translate("portName")}
                value={shipmentTracking?.shipmentTracking?.finalPort ?? ""}
                disabled
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Agent section */}
      {renderAgentFields()}

      {/* Customer details section */}
      <Title item xs={12} sm={12} md={12} lg={12}>
        <span>{translate("customerDetails")}</span>
      </Title>
      {customers?.map((customer, idx) => (
        <>
          <CustomerDetailsComponent
            key={`customer-${idx}`}
            indexOf={idx}
            getHasError={(error: string) => customerHasErrors(error)}
            getError={(error: string) => getCustomerErrors(error)}
            customerDetails={customer}
            customerList={customerList}
            customerCommunication={customersCommunications?.find(
              (communication) => communication.customerId === customer?.customer?.id
            )}
            updateCustomer={(customerDetails: CustomerDetails) => {
              const customersToUpdate = customers || [];

              if (customersToUpdate && customersToUpdate[idx])
                customersToUpdate[idx] = customerDetails;

              setCustomers([...customersToUpdate]);
              updateShipmentTracking({
                ...shipmentTracking,
                customersToShipment: [...customersToUpdate],
              });
            }}
            updateCommunication={(communication) =>
              onUpdatedCustomerCommunication(
                customer,
                customersCommunications,
                communication ?? ({} as Communication),
                (communications) => {
                  setCommunications([
                    ...usersCommunications,
                    ...communications,
                    ...contactCommunication,
                  ]);
                  updateShipmentTracking({
                    ...shipmentTracking,
                    communications: [
                      ...usersCommunications,
                      ...communications,
                      ...contactCommunication,
                    ],
                  });
                }
              )
            }
            addNewCustomer={() => {
              if (customers) {
                setCustomers([
                  ...customers,
                  { order: "", customer: { email: "", phone: "" } as CustomerShipmentTracking },
                ]);
              }
            }}
            deleteCustomer={() => {
              const newCustomers = customers || [];
              const newCommunications = communications || [];

              if (newCustomers && newCommunications) {
                const oldCustomer = newCustomers?.[idx];

                if (oldCustomer?.customer?.id) {
                  const oldCommunicationIndex = newCommunications?.findIndex(
                    (communication) => communication.customerId === oldCustomer?.customer?.id
                  );

                  if (oldCommunicationIndex !== -1) {
                    newCommunications?.splice(oldCommunicationIndex, 1);
                  }
                }

                newCustomers.splice(idx, 1);

                setCustomers([...newCustomers]);
                setCommunications([...newCommunications]);

                updateShipmentTracking({
                  ...shipmentTracking,
                  customers: [...newCustomers],
                  communications: [...newCommunications],
                });
              }
            }}
          />
        </>
      ))}

      <Title item xs={12} sm={12} md={12} lg={12}>
        <span>{translate("communicationSubscriptions")}</span>
      </Title>

      {usersCommunications.map((communication, index) => {
        return (
          <Grid
            key={index}
            container
            direction="row"
            spacing={3}
            className={`${classes.mb16} name${index}`}
          >
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <AutoSuggest
                dataCy="communicationUser"
                label={translate("user")}
                name="user"
                inputValue={getUserName(communication.userId, communication.user)}
                value={communication.userId}
                getOptionLabel={(user?: User) =>
                  user ? `${user.name ?? ""} ${user.lastName ?? ""}` : ""
                }
                getOptionSelected={(option: User, value: User) => option.id === value.id}
                placeholder={translate("firstName")}
                options={usersList}
                onChange={(user: User) => {
                  handleChangeCommunication("userId", index, user?.id);
                }}
                error={
                  !!fieldError.length &&
                  (usersCommunications?.[index]?.userId === 0 ||
                    !usersCommunications?.[index]?.userId)
                }
                errorMessage={usersCommunications[index].userId === 0 ? fieldError : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <AutoSuggest
                dataCy="autosuggestFromValue"
                label={translate("via")}
                placeholder={
                  getUserAutosuggestValue(index)?.length === 0 ? translate("selectAnOption") : ""
                }
                getOptionLabel={(option: Options) => {
                  return option.value;
                }}
                getOptionSelected={(option: Options, value: Options) =>
                  option?.value === value?.value
                }
                value={getUserAutosuggestValue(index)}
                multiple
                onChange={(options: Options[]) => {
                  const valueOptions = options.map((option: any) => option.value);

                  if (
                    valueOptions.includes(CommunicationsFromOptions.SMS) &&
                    valueOptions.includes(CommunicationsFromOptions.EMAIL)
                  )
                    handleChangeCommunication("from", index, CommunicationsFromOptions.BOTH);
                  else if (
                    valueOptions.includes(CommunicationsFromOptions.SMS) &&
                    !valueOptions.includes(CommunicationsFromOptions.EMAIL)
                  )
                    handleChangeCommunication("from", index, CommunicationsFromOptions.SMS);
                  else if (
                    !valueOptions.includes(CommunicationsFromOptions.SMS) &&
                    valueOptions.includes(CommunicationsFromOptions.EMAIL)
                  )
                    handleChangeCommunication("from", index, CommunicationsFromOptions.EMAIL);
                  else if (options.length === 0) {
                    handleChangeCommunication("from", index, "");
                  }
                }}
                options={viaOptions}
                error={!!fieldError.length && usersCommunications?.[index]?.from === ""}
                errorMessage={usersCommunications[index].from === "" ? fieldError : ""}
              />
            </Grid>

            {renderAddOrDeleteUserCommunication(index)}
          </Grid>
        );
      })}

      <Buttons>
        <ActionButtons
          cancelDisabled={isUpdatingAction}
          loading={isUpdatingAction}
          onCancel={onCancel}
          onSave={() => {
            handleSave();
          }}
          textSave={!isCreate ? translate("save") : translate("create")}
        />
      </Buttons>
    </Grid>
  );
};

export { SkeletonDetails };
export default Details;
